/**
 * EarlyPaymentResult
 * Shows the result of a successful early payment operation.
 */

import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import type { EarlyPaymentResponse, EarlyPaymentType } from '@/types/early-payment.types';

interface EarlyPaymentResultProps {
  result: EarlyPaymentResponse;
  paymentType: EarlyPaymentType;
  onClose: () => void;
}

export function EarlyPaymentResult({ result, paymentType, onClose }: EarlyPaymentResultProps) {
  const typeLabels: Record<EarlyPaymentType, string> = {
    ANTICIPATED: 'Abono a Capital',
    ADVANCED: 'Pago Adelantado',
    SETTLEMENT: 'Liquidacion',
  };

  return (
    <div className="flex flex-col items-center py-4">
      <CheckCircle2 size={48} className="mb-4 text-green-500" />

      <h3 className="text-lg font-semibold text-foreground">
        {typeLabels[paymentType]} Exitoso
      </h3>

      <p className="mt-2 text-center text-sm text-muted-foreground">
        {result.message}
      </p>

      <div className="mt-6 w-full space-y-3 rounded-lg border border-border bg-muted/50 p-4">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Monto Aplicado</span>
          <span className="text-sm font-medium tabular-nums text-foreground">
            {formatCurrency(result.amount_applied_mxn, 'MXN', 'es-MX')}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Nuevo Saldo</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium tabular-nums text-foreground">
              {formatCurrency(result.new_balance_mxn, 'MXN', 'es-MX')}
            </span>
            {result.new_balance_mxn === 0 && (
              <Badge variant="success">LIQUIDADO</Badge>
            )}
          </div>
        </div>
      </div>

      <Button className="mt-6" onClick={onClose} fullWidth>
        Cerrar
      </Button>
    </div>
  );
}
